import type { Track } from '../interfaces/track';

export interface HistoryTrackItem {
  id: string;
  track: Track;
  playedAt: number;
  contextUri?: string;
  contextType?: 'album' | 'playlist' | 'artist' | 'liked' | 'track';
  contextName?: string;
}

export interface HistorySessionGroup {
  id: string;
  type: 'album' | 'liked' | 'daily';
  title: string;
  subtitle: string;
  imageUrl?: string;
  badge?: 'liked' | null;
  isStacked?: boolean;
  trackCount: number;
  tracks: Track[];
  playedAt: number;
  contextUri?: string;
}

export interface HistoryDateGroup {
  dateKey: string; // "Today", "Yesterday", "Fri, Sep 18"
  timestamp: number;
  sessions: HistorySessionGroup[];
}

const HISTORY_STORAGE_KEY = 'spotify_play_history';
const MAX_HISTORY_ITEMS = 500;

export function formatDateGroup(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();

  const isToday =
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear();

  if (isToday) return 'Today';

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isYesterday) return 'Yesterday';

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${weekday}, ${month} ${day}`;
}

export function getRawHistory(): HistoryTrackItem[] {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Failed to parse history:', e);
  }

  return seedFromRecentlyPlayed();
}

export function saveRawHistory(items: HistoryTrackItem[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items.slice(0, MAX_HISTORY_ITEMS)));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('spotify_history_updated'));
    }
  } catch (e) {
    console.warn('Failed to save play history:', e);
  }
}

export function recordPlay(track: any, contextUri?: string, contextName?: string): void {
  if (!track || !track.id) return;
  const history = getRawHistory();

  // Deduplicate rapid consecutive plays of the same song within 4 seconds
  if (
    history.length > 0 &&
    history[0].track?.id === track.id &&
    Date.now() - history[0].playedAt < 4000
  ) {
    return;
  }

  const newItem: HistoryTrackItem = {
    id: `${track.id}_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    track,
    playedAt: Date.now(),
    contextUri: contextUri || track.album?.uri || '',
    contextName: contextName || track.album?.name,
    contextType: contextUri?.includes('playlist')
      ? 'playlist'
      : contextUri?.includes('album')
      ? 'album'
      : 'track',
  };

  history.unshift(newItem);
  saveRawHistory(history);
}

function seedFromRecentlyPlayed(): HistoryTrackItem[] {
  try {
    const raw = localStorage.getItem('recentlyPlayed');
    if (!raw) return [];
    const items = JSON.parse(raw);
    if (!Array.isArray(items) || items.length === 0) return [];

    const seeded: HistoryTrackItem[] = [];
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    items.forEach((item: any, idx: number) => {
      const track = item.type === 'track' || item.album ? (item as Track) : null;
      if (!track) return;

      let timeOffset = 0;
      if (idx < 2) {
        timeOffset = idx * 15 * 60 * 1000;
      } else if (idx < 5) {
        timeOffset = oneDay + (idx - 2) * 25 * 60 * 1000;
      } else {
        timeOffset = 2 * oneDay + (idx - 5) * 40 * 60 * 1000;
      }

      seeded.push({
        id: `seed_${track.id}_${idx}`,
        track,
        playedAt: now - timeOffset,
        contextUri: track.album?.uri || '',
        contextName: track.album?.name,
        contextType: 'album',
      });
    });

    if (seeded.length > 0) {
      try {
        localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(seeded));
      } catch (e) {}
    }
    return seeded;
  } catch (e) {
    return [];
  }
}

export function getGroupedHistory(): HistoryDateGroup[] {
  const items = getRawHistory();
  if (!items.length) return [];

  // Sort descending by playedAt (newest first)
  const sorted = [...items].sort((a, b) => b.playedAt - a.playedAt);

  // Group by date string (Today, Yesterday, Fri, Sep 18, etc.)
  const dateMap: Map<string, { timestamp: number; items: HistoryTrackItem[] }> = new Map();

  for (const item of sorted) {
    const key = formatDateGroup(item.playedAt);
    if (!dateMap.has(key)) {
      dateMap.set(key, { timestamp: item.playedAt, items: [] });
    }
    dateMap.get(key)!.items.push(item);
  }

  const dateGroups: HistoryDateGroup[] = [];

  dateMap.forEach(({ timestamp, items: dayItems }, dateKey) => {
    const sessions: HistorySessionGroup[] = [];

    // 1. Check for albums where 2 or more songs were played on this day
    const albumMap: Map<string, { name: string; artist: string; image: string; items: HistoryTrackItem[] }> = new Map();

    for (const h of dayItems) {
      const albumKey = h.track.album?.id || h.track.album?.name;
      if (albumKey) {
        if (!albumMap.has(albumKey)) {
          albumMap.set(albumKey, {
            name: h.track.album?.name || 'Album',
            artist: h.track.artists?.[0]?.name || h.track.album?.artists?.[0]?.name || 'Artist',
            image:
              h.track.album?.images?.[0]?.url ||
              h.track.album?.images?.[1]?.url ||
              '',
            items: [],
          });
        }
        albumMap.get(albumKey)!.items.push(h);
      }
    }

    albumMap.forEach(({ name, artist, image, items: albumItems }, albumKey) => {
      // ONLY create album session when 2 or more songs played from the same album
      if (albumItems.length >= 2) {
        sessions.push({
          id: `album_${albumKey}_${dateKey}`,
          type: 'album',
          title: name,
          subtitle: `Album • ${artist} • ${albumItems.length} songs played`,
          imageUrl: image,
          trackCount: albumItems.length,
          tracks: albumItems.map((h) => h.track),
          playedAt: albumItems[0].playedAt,
          contextUri: albumItems[0].contextUri,
        });
      }
    });

    // 2. Check for Liked Songs added on this day
    const likedItems = dayItems.filter((h) => h.contextType === 'liked');
    if (likedItems.length > 0) {
      sessions.push({
        id: `liked_${dateKey}`,
        type: 'liked',
        title: 'Liked Songs',
        subtitle: `${likedItems.length} ${likedItems.length === 1 ? 'song added' : 'songs added'}`,
        badge: 'liked',
        imageUrl:
          likedItems[0].track.album?.images?.[0]?.url ||
          likedItems[0].track.album?.images?.[1]?.url ||
          '',
        trackCount: likedItems.length,
        tracks: likedItems.map((h) => h.track),
        playedAt: likedItems[0].playedAt,
      });
    }

    // 3. Daily summary row: ALWAYS shows dropdown of all songs played that day!
    const allDayTracks = dayItems.map((h) => h.track);
    const dayCover =
      dayItems[0].track.album?.images?.[0]?.url ||
      dayItems[0].track.album?.images?.[1]?.url ||
      '';

    sessions.push({
      id: `daily_${dateKey}`,
      type: 'daily',
      title: `${dayItems.length} ${dayItems.length === 1 ? 'song played' : 'songs played'}`,
      subtitle: '',
      imageUrl: dayCover,
      isStacked: dayItems.length > 1,
      trackCount: dayItems.length,
      tracks: allDayTracks,
      playedAt: dayItems[0].playedAt,
    });

    dateGroups.push({
      dateKey,
      timestamp,
      sessions,
    });
  });

  return dateGroups;
}

export const historyService = {
  recordPlay,
  getRawHistory,
  saveRawHistory,
  getGroupedHistory,
  formatDateGroup,
};

export default historyService;
